import { create } from 'zustand';
import { supabase, reportSupabaseError } from '@/lib/supabase';
import type { Collection } from '@/types';

/** @deprecated Use useClassStore instead. Kept for backwards compatibility during migration. */
interface CollectionState {
  collections: Collection[];
  currentCollection: Collection | null;
  isLoading: boolean;
  error: string | null;
  setCollections: (collections: Collection[]) => void;
  setCurrentCollection: (collection: Collection | null) => void;
  fetchCollections: (userId: string) => Promise<void>;
  createCollection: (userId: string, name: string, description?: string, color?: string) => Promise<Collection | null>;
  updateCollection: (collectionId: string, updates: Partial<Collection>) => Promise<void>;
  deleteCollection: (collectionId: string) => Promise<void>;
  addDocuments: (collectionId: string, documentIds: string[]) => Promise<void>;
  removeDocument: (collectionId: string, documentId: string) => Promise<void>;
}

/** @deprecated Use useClassStore instead. Kept for backwards compatibility during migration. */
export const useCollectionStore = create<CollectionState>((set, get) => ({
  collections: [],
  currentCollection: null,
  isLoading: false,
  error: null,

  setCollections: (collections) => set({ collections }),
  setCurrentCollection: (currentCollection) => set({ currentCollection }),

  fetchCollections: async (userId: string) => {
    set({ isLoading: true, error: null });
    const { data, error } = await supabase
      .from('collections')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      reportSupabaseError(error, 'fetchCollections');
      set({ error: error.message, isLoading: false });
      return;
    }

    set({ collections: data || [], isLoading: false });
  },

  createCollection: async (userId: string, name: string, description?: string, color?: string) => {
    const newCollection = {
      user_id: userId,
      name,
      description,
      color: color || 'blue',
      document_ids: [],
    };

    const { data, error } = await supabase
      .from('collections')
      .insert(newCollection)
      .select()
      .single();

    if (error) {
      reportSupabaseError(error, 'collectionStore.createCollection');
      set({ error: error.message });
      return null;
    }

    set((state) => ({
      collections: [data, ...state.collections],
    }));

    return data;
  },

  updateCollection: async (collectionId: string, updates: Partial<Collection>) => {
    const { error } = await supabase
      .from('collections')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', collectionId);

    if (error) {
      reportSupabaseError(error, 'collectionStore.updateCollection');
      set({ error: error.message });
      return;
    }

    set((state) => ({
      collections: state.collections.map((c) =>
        c.id === collectionId ? { ...c, ...updates } : c
      ),
      currentCollection:
        state.currentCollection?.id === collectionId
          ? { ...state.currentCollection, ...updates }
          : state.currentCollection,
    }));
  },

  deleteCollection: async (collectionId: string) => {
    const { error } = await supabase
      .from('collections')
      .delete()
      .eq('id', collectionId);

    if (error) {
      reportSupabaseError(error, 'collectionStore.deleteCollection');
      set({ error: error.message });
      return;
    }

    set((state) => ({
      collections: state.collections.filter((c) => c.id !== collectionId),
      currentCollection:
        state.currentCollection?.id === collectionId ? null : state.currentCollection,
    }));
  },

  addDocuments: async (collectionId: string, documentIds: string[]) => {
    const collection = get().collections.find((c) => c.id === collectionId);
    if (!collection) return;

    const newDocIds = [...new Set([...collection.document_ids, ...documentIds])];

    const { error } = await supabase
      .from('collections')
      .update({ document_ids: newDocIds, updated_at: new Date().toISOString() })
      .eq('id', collectionId);

    if (error) {
      reportSupabaseError(error, 'collectionStore.addDocuments');
      set({ error: error.message });
      return;
    }

    set((state) => ({
      collections: state.collections.map((c) =>
        c.id === collectionId ? { ...c, document_ids: newDocIds } : c
      ),
      currentCollection:
        state.currentCollection?.id === collectionId
          ? { ...state.currentCollection, document_ids: newDocIds }
          : state.currentCollection,
    }));
  },

  removeDocument: async (collectionId: string, documentId: string) => {
    const collection = get().collections.find((c) => c.id === collectionId);
    if (!collection) return;

    const newDocIds = collection.document_ids.filter((id) => id !== documentId);

    const { error } = await supabase
      .from('collections')
      .update({ document_ids: newDocIds, updated_at: new Date().toISOString() })
      .eq('id', collectionId);

    if (error) {
      reportSupabaseError(error, 'collectionStore.removeDocument');
      set({ error: error.message });
      return;
    }

    set((state) => ({
      collections: state.collections.map((c) =>
        c.id === collectionId ? { ...c, document_ids: newDocIds } : c
      ),
      currentCollection:
        state.currentCollection?.id === collectionId
          ? { ...state.currentCollection, document_ids: newDocIds }
          : state.currentCollection,
    }));
  },
}));
